import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Card,
  Descriptions,
  Tabs,
  Button,
  Space,
  Input,
  Form,
  InputNumber,
  DatePicker,
  Modal,
  Progress,
  Tag,
  Spin,
  Statistic,
  Row,
  Col,
  Divider,
  message,
  Tooltip,
} from 'antd';
import { Link } from 'react-router-dom';
import {
  PauseCircleOutlined,
  PlayCircleOutlined,
  StopOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type {
  CampaignDetail,
  Campaign,
  LedgerRow,
  StatsPoint,
} from '../../services/adsService';
import {
  fetchAdsCampaignById,
  fetchAdsCampaignLedger,
  pauseAdsCampaign,
  resumeAdsCampaign,
  endAdsCampaign,
  postCampaignNote,
  updateAdsCampaign,
  fetchCampaignStats,
} from '../../services/adsService';
import { LedgerTable } from './LedgerTable';
import {
  formatPoints,
  formatDateTime,
  statusLabel,
  typeLabel,
  STATUS_COLORS,
  TYPE_COLORS,
} from '../../utils/adsHelpers';

const { TextArea } = Input;

interface AdsDrawerProps {
  campaignId: string | null;
  open: boolean;
  onClose: () => void;
  onRefresh: () => void;
}

function formatPeriodLabel(
  period: string,
  range: '24h' | '7d' | '30d'
): string {
  const d = new Date(period);
  if (range === '24h') {
    return `${d.getHours().toString().padStart(2, '0')}:00`;
  }
  return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d
    .getDate()
    .toString()
    .padStart(2, '0')}`;
}

export const AdsDrawer: React.FC<AdsDrawerProps> = ({
  campaignId,
  open,
  onClose,
  onRefresh,
}) => {
  const [detail, setDetail] = useState<CampaignDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ledger, setLedger] = useState<LedgerRow[]>([]);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerPageSize, setLedgerPageSize] = useState(20);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [statsRange, setStatsRange] = useState<'24h' | '7d' | '30d'>('7d');
  const [statsData, setStatsData] = useState<StatsPoint[]>([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [adminNote, setAdminNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [endModalOpen, setEndModalOpen] = useState(false);
  const [pendingEndId, setPendingEndId] = useState<string | null>(null);

  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [editForm] = Form.useForm();
  const [editSaving, setEditSaving] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!campaignId || typeof campaignId !== 'string') return;
    const id = String(campaignId).trim();
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const d = await fetchAdsCampaignById(id);
      setDetail(d);
      setAdminNote(d.campaign.adminNote ?? '');
    } catch (e: unknown) {
      setDetail(null);
      const err = e as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Không tìm thấy chiến dịch';
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  const loadStats = useCallback(
    async (range: '24h' | '7d' | '30d') => {
      if (!campaignId) return;
      setStatsLoading(true);
      try {
        const res = await fetchCampaignStats(campaignId, range);
        setStatsData(res.series);
      } catch {
        setStatsData([]);
      } finally {
        setStatsLoading(false);
      }
    },
    [campaignId]
  );

  useEffect(() => {
    if (open && campaignId) {
      loadDetail();
      loadStats(statsRange);
      setEditMode(false);
    } else {
      setDetail(null);
      setLoadError(null);
      setStatsData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaignId]);

  useEffect(() => {
    if (open && campaignId) {
      loadStats(statsRange);
    }
  }, [open, campaignId, statsRange, loadStats]);

  useEffect(() => {
    if (open && campaignId) {
      setLedgerLoading(true);
      fetchAdsCampaignLedger(campaignId, ledgerPage, ledgerPageSize)
        .then(res => {
          setLedger(res.data);
          setLedgerTotal(res.meta.total);
        })
        .catch(() => setLedger([]))
        .finally(() => setLedgerLoading(false));
    }
  }, [open, campaignId, ledgerPage, ledgerPageSize]);

  const campaign = detail?.campaign;

  const handlePause = async () => {
    if (!campaignId) return;
    setActionLoading(true);
    try {
      await pauseAdsCampaign(campaignId);
      message.success('Đã tạm dừng chiến dịch');
      if (detail)
        setDetail({
          ...detail,
          campaign: { ...detail.campaign, status: 'PAUSED' },
        });
      onRefresh();
      loadDetail();
    } catch {
      message.error('Không thể tạm dừng');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    if (!campaignId || !campaign) return;
    if (campaign.remainingPoints < campaign.costPerSuccessOrderPoints) return;
    setActionLoading(true);
    try {
      await resumeAdsCampaign(campaignId);
      message.success('Đã kích hoạt chiến dịch');
      if (detail)
        setDetail({
          ...detail,
          campaign: { ...detail.campaign, status: 'ACTIVE' },
        });
      onRefresh();
      loadDetail();
    } catch {
      message.error('Không thể tiếp tục');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEndClick = () => {
    setPendingEndId(campaignId);
    setEndModalOpen(true);
  };

  const handleEndConfirm = async () => {
    if (!pendingEndId) return;
    setActionLoading(true);
    try {
      await endAdsCampaign(pendingEndId);
      message.success('Đã kết thúc chiến dịch');
      setEndModalOpen(false);
      setPendingEndId(null);
      if (detail && detail.campaign.id === pendingEndId) {
        setDetail({
          ...detail,
          campaign: { ...detail.campaign, status: 'ENDED' },
        });
      }
      onRefresh();
      loadDetail();
    } catch {
      message.error('Không thể kết thúc');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveNote = async () => {
    if (!campaignId || !adminNote.trim()) return;
    setNoteSaving(true);
    try {
      await postCampaignNote(campaignId, adminNote.trim());
      message.success('Đã lưu ghi chú');
      loadDetail();
    } catch {
      message.error('Không thể lưu ghi chú');
    } finally {
      setNoteSaving(false);
    }
  };

  const handleEditOpen = () => {
    if (!campaign) return;
    editForm.setFieldsValue({
      title: campaign.title,
      total_budget_points: campaign.totalBudgetPoints,
      cost_per_success_order_points: campaign.costPerSuccessOrderPoints,
      daily_budget_points: campaign.dailyBudgetPoints ?? null,
      targeting_radius_km: campaign.radiusKm ?? 3,
      start_at: campaign.startAt ? dayjs(campaign.startAt) : null,
      end_at: campaign.endAt ? dayjs(campaign.endAt) : null,
    });
    setEditMode(true);
  };

  const handleEditSave = async () => {
    if (!campaignId) return;
    try {
      const values = await editForm.validateFields();
      setEditSaving(true);
      await updateAdsCampaign(campaignId, {
        title: values.title,
        total_budget_points: values.total_budget_points,
        cost_per_success_order_points: values.cost_per_success_order_points,
        daily_budget_points: values.daily_budget_points || null,
        targeting_radius_km: values.targeting_radius_km,
        start_at: values.start_at ? values.start_at.toISOString() : null,
        end_at: values.end_at ? values.end_at.toISOString() : null,
      });
      message.success('Đã cập nhật chiến dịch');
      setEditMode(false);
      onRefresh();
      loadDetail();
    } catch (e: unknown) {
      const err = e as { message?: string };
      if (err?.message) message.error(err.message);
    } finally {
      setEditSaving(false);
    }
  };

  const canResume =
    campaign &&
    (campaign.status === 'PAUSED' || campaign.status === 'DRAFT') &&
    campaign.remainingPoints >= campaign.costPerSuccessOrderPoints;

  const spendPercent =
    campaign && campaign.totalBudgetPoints > 0
      ? Math.min((campaign.spentPoints / campaign.totalBudgetPoints) * 100, 100)
      : 0;

  const chartDataFormatted = statsData.map(pt => ({
    ...pt,
    label: formatPeriodLabel(pt.period, statsRange),
  }));

  if (!open) return null;

  return (
    <Drawer
      title={
        <Space>
          <span>Chi tiết chiến dịch</span>
          {campaign && (
            <Tag color={STATUS_COLORS[campaign.status]}>
              {statusLabel(campaign.status)}
            </Tag>
          )}
        </Space>
      }
      placement="right"
      width={600}
      open={open}
      onClose={() => {
        setEditMode(false);
        onClose();
      }}
      destroyOnClose
      extra={
        campaign && !campaign.status.includes('ENDED') && !editMode ? (
          <Tooltip title="Chỉnh sửa">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={handleEditOpen}
            >
              Sửa
            </Button>
          </Tooltip>
        ) : editMode ? (
          <Space>
            <Button
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setEditMode(false)}
            >
              Hủy
            </Button>
            <Button
              size="small"
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleEditSave}
              loading={editSaving}
            >
              Lưu
            </Button>
          </Space>
        ) : null
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      ) : campaign ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* Tổng quan */}
          <Card
            size="small"
            title="Tổng quan"
            extra={
              <Button
                size="small"
                icon={<ReloadOutlined />}
                type="text"
                onClick={loadDetail}
              />
            }
          >
            {editMode ? (
              <Form form={editForm} layout="vertical" size="small">
                <Form.Item
                  name="title"
                  label="Tên chiến dịch"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      name="total_budget_points"
                      label="Ngân sách (điểm)"
                      rules={[{ required: true }]}
                    >
                      <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="cost_per_success_order_points"
                      label="CPSO (điểm)"
                      rules={[{ required: true }]}
                    >
                      <InputNumber min={0} max={10} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      name="daily_budget_points"
                      label="Ngân sách/ngày (điểm)"
                    >
                      <InputNumber
                        min={0}
                        style={{ width: '100%' }}
                        placeholder="Không giới hạn"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="targeting_radius_km" label="Bán kính (km)">
                      <InputNumber
                        min={0.5}
                        max={10}
                        step={0.5}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="start_at" label="Bắt đầu">
                      <DatePicker
                        showTime
                        style={{ width: '100%' }}
                        format="DD/MM/YYYY HH:mm"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="end_at" label="Kết thúc">
                      <DatePicker
                        showTime
                        style={{ width: '100%' }}
                        format="DD/MM/YYYY HH:mm"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            ) : (
              <Descriptions column={2} size="small">
                <Descriptions.Item label="ID" span={2}>
                  <span style={{ fontSize: 11, color: '#888' }}>
                    {campaign.id}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="Tên" span={2}>
                  <strong>{campaign.title}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Loại">
                  <Tag color={TYPE_COLORS[campaign.type]}>
                    {typeLabel(campaign.type)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Trạng thái">
                  <Tag color={STATUS_COLORS[campaign.status]}>
                    {statusLabel(campaign.status)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Merchant" span={2}>
                  {detail?.merchant ? (
                    <Space direction="vertical" size={0}>
                      <Link to={`/merchants/${campaign.merchantId}`}>
                        <strong>{detail.merchant.name}</strong>
                      </Link>
                      {detail.merchant.phone && (
                        <span style={{ fontSize: 12, color: '#888' }}>
                          {detail.merchant.phone}
                        </span>
                      )}
                    </Space>
                  ) : (
                    <span>—</span>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Bắt đầu">
                  {campaign.startAt ? formatDateTime(campaign.startAt) : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Kết thúc">
                  {campaign.endAt ? formatDateTime(campaign.endAt) : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Tạo lúc">
                  {formatDateTime(campaign.createdAt)}
                </Descriptions.Item>
                <Descriptions.Item label="Cập nhật">
                  {formatDateTime(campaign.updatedAt)}
                </Descriptions.Item>
              </Descriptions>
            )}
          </Card>

          {/* Ngân sách */}
          <Card size="small" title="Ngân sách & Chi tiêu">
            <Row gutter={16} style={{ marginBottom: 12 }}>
              <Col span={8}>
                <Statistic
                  title="Tổng ngân sách"
                  value={campaign.totalBudgetPoints}
                  suffix="đ"
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Đã chi"
                  value={campaign.spentPoints}
                  suffix="đ"
                  valueStyle={{ fontSize: 16, color: '#fa8c16' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Còn lại"
                  value={campaign.remainingPoints}
                  suffix="đ"
                  valueStyle={{
                    fontSize: 16,
                    color:
                      campaign.remainingPoints <
                      campaign.costPerSuccessOrderPoints
                        ? '#ff4d4f'
                        : '#52c41a',
                  }}
                />
              </Col>
            </Row>
            <Progress
              percent={Math.round(spendPercent)}
              status={
                campaign.remainingPoints < campaign.costPerSuccessOrderPoints
                  ? 'exception'
                  : 'active'
              }
              strokeColor={
                spendPercent >= 90
                  ? '#ff4d4f'
                  : spendPercent >= 70
                  ? '#fa8c16'
                  : '#1890ff'
              }
            />
            <Descriptions column={2} size="small" style={{ marginTop: 8 }}>
              <Descriptions.Item label="CPSO">
                {formatPoints(campaign.costPerSuccessOrderPoints)}
              </Descriptions.Item>
              {campaign.dailyBudgetPoints != null && (
                <Descriptions.Item label="Ngân sách/ngày">
                  {formatPoints(campaign.dailyBudgetPoints)}
                </Descriptions.Item>
              )}
              {campaign.type === 'SHOP' && (
                <Descriptions.Item label="Bán kính">
                  {campaign.radiusKm ?? 3} km
                </Descriptions.Item>
              )}
              {campaign.type === 'ITEM' && campaign.itemCount != null && (
                <Descriptions.Item label="Số món">
                  {campaign.itemCount} món
                </Descriptions.Item>
              )}
              {campaign.autoPauseReason && (
                <Descriptions.Item label="Lý do tạm dừng" span={2}>
                  <span style={{ color: '#ff4d4f' }}>
                    {campaign.autoPauseReason}
                  </span>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Hiệu suất */}
          <Card size="small" title="Hiệu suất">
            <Tabs
              defaultActiveKey="stats"
              items={[
                {
                  key: 'stats',
                  label: 'Thống kê',
                  children: (
                    <>
                      <Row gutter={16} style={{ marginBottom: 12 }}>
                        <Col span={8}>
                          <Statistic
                            title="Tổng clicks"
                            value={detail?.stats?.totals?.clicks ?? 0}
                            valueStyle={{ fontSize: 16 }}
                          />
                        </Col>
                        <Col span={8}>
                          <Statistic
                            title="Đơn thành công"
                            value={detail?.stats?.totals?.attributedOrders ?? 0}
                            valueStyle={{ fontSize: 16, color: '#52c41a' }}
                          />
                        </Col>
                        <Col span={8}>
                          <Statistic
                            title="Tỷ lệ chuyển đổi"
                            value={(
                              detail?.stats?.totals?.conversionRate ?? 0
                            ).toFixed(1)}
                            suffix="%"
                            valueStyle={{ fontSize: 16 }}
                          />
                        </Col>
                      </Row>
                      <Divider style={{ margin: '8px 0' }} />
                      <Space style={{ marginBottom: 8 }}>
                        <span style={{ fontSize: 12, color: '#888' }}>
                          Xem theo:
                        </span>
                        {(['24h', '7d', '30d'] as const).map(r => (
                          <Button
                            key={r}
                            size="small"
                            type={statsRange === r ? 'primary' : 'default'}
                            onClick={() => setStatsRange(r)}
                          >
                            {r === '24h'
                              ? '24 giờ'
                              : r === '7d'
                              ? '7 ngày'
                              : '30 ngày'}
                          </Button>
                        ))}
                      </Space>
                      <div style={{ height: 200, position: 'relative' }}>
                        {statsLoading && (
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'rgba(255,255,255,0.7)',
                              zIndex: 1,
                            }}
                          >
                            <Spin />
                          </div>
                        )}
                        {!statsLoading && chartDataFormatted.length === 0 ? (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '100%',
                              color: '#aaa',
                              fontSize: 13,
                            }}
                          >
                            Không có dữ liệu trong khoảng thời gian này
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                              data={chartDataFormatted}
                              margin={{
                                top: 5,
                                right: 10,
                                left: -20,
                                bottom: 0,
                              }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                              <YAxis tick={{ fontSize: 11 }} />
                              <ReTooltip />
                              <Legend
                                iconSize={10}
                                wrapperStyle={{ fontSize: 11 }}
                              />
                              <Line
                                type="monotone"
                                dataKey="clicks"
                                stroke="#1890ff"
                                name="Clicks"
                                dot={false}
                                strokeWidth={2}
                              />
                              <Line
                                type="monotone"
                                dataKey="orders"
                                stroke="#52c41a"
                                name="Đơn hàng"
                                dot={false}
                                strokeWidth={2}
                              />
                              <Line
                                type="monotone"
                                dataKey="spend"
                                stroke="#fa8c16"
                                name="Chi tiêu (đ)"
                                dot={false}
                                strokeWidth={2}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </>
                  ),
                },
                {
                  key: 'ledger',
                  label: `Billing Ledger ${
                    ledgerTotal > 0 ? `(${ledgerTotal})` : ''
                  }`,
                  children: (
                    <LedgerTable
                      data={ledger}
                      loading={ledgerLoading}
                      page={ledgerPage}
                      pageSize={ledgerPageSize}
                      total={ledgerTotal}
                      onPageChange={(p, s) => {
                        setLedgerPage(p);
                        if (s) setLedgerPageSize(s);
                      }}
                    />
                  ),
                },
              ]}
            />
          </Card>

          {/* Actions */}
          {!editMode && (
            <Card size="small" title="Thao tác quản trị">
              <Space wrap style={{ marginBottom: 12 }}>
                {campaign.status === 'ACTIVE' && (
                  <Button
                    icon={<PauseCircleOutlined />}
                    onClick={handlePause}
                    loading={actionLoading}
                  >
                    Tạm dừng
                  </Button>
                )}
                {canResume && (
                  <Button
                    icon={<PlayCircleOutlined />}
                    type="primary"
                    onClick={handleResume}
                    loading={actionLoading}
                  >
                    Kích hoạt
                  </Button>
                )}
                {!canResume &&
                  (campaign.status === 'PAUSED' ||
                    campaign.status === 'DRAFT') && (
                    <Tooltip title="Ngân sách còn lại < CPSO">
                      <Button icon={<PlayCircleOutlined />} disabled>
                        Kích hoạt
                      </Button>
                    </Tooltip>
                  )}
                {['ACTIVE', 'PAUSED', 'DRAFT'].includes(campaign.status) && (
                  <Button
                    icon={<StopOutlined />}
                    danger
                    onClick={handleEndClick}
                    loading={actionLoading}
                  >
                    Kết thúc
                  </Button>
                )}
              </Space>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ fontSize: 12, marginBottom: 4, color: '#888' }}>
                Ghi chú admin
              </div>
              <TextArea
                rows={2}
                value={adminNote}
                onChange={e => setAdminNote(e.target.value)}
                placeholder="Thêm ghi chú..."
              />
              <Button
                type="primary"
                size="small"
                style={{ marginTop: 8 }}
                onClick={handleSaveNote}
                loading={noteSaving}
                disabled={!adminNote.trim()}
              >
                Lưu ghi chú
              </Button>
              {campaign.adminNote && (
                <div
                  style={{
                    marginTop: 8,
                    padding: '6px 10px',
                    background: '#fffbe6',
                    border: '1px solid #ffe58f',
                    borderRadius: 4,
                    fontSize: 12,
                    color: '#614700',
                  }}
                >
                  <strong>Ghi chú hiện tại:</strong> {campaign.adminNote}
                </div>
              )}
            </Card>
          )}
        </Space>
      ) : (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <p style={{ color: '#666', marginBottom: 8 }}>
            Không tìm thấy chiến dịch
          </p>
          {loadError && (
            <p
              style={{
                fontSize: 12,
                color: '#999',
                maxWidth: 360,
                margin: '0 auto',
              }}
            >
              {loadError}
            </p>
          )}
        </div>
      )}

      <Modal
        title="Xác nhận kết thúc chiến dịch"
        open={endModalOpen}
        onOk={handleEndConfirm}
        confirmLoading={actionLoading}
        onCancel={() => {
          setEndModalOpen(false);
          setPendingEndId(null);
        }}
        okText="Kết thúc"
        cancelText="Hủy"
        okButtonProps={{ danger: true }}
      >
        <p>
          Bạn có chắc muốn kết thúc chiến dịch này? Hành động không thể hoàn
          tác.
        </p>
      </Modal>
    </Drawer>
  );
};
